/**
 * Admin API Client
 */

import {AppBskyEmbedExternal, AppBskyEmbedImages, AppBskyRichtextFacet} from '@atproto/api';
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON
} from '@simplewebauthn/browser';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Export API_BASE_URL for direct access
export const getApiBaseUrl = (): string => API_BASE_URL;

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  RESTRICTED = 'RESTRICTED',
  SUSPENDED = 'SUSPENDED',
  BLOCKED = 'BLOCKED'
}

// Admin authentication types
export interface AdminAuthResult {
  token: string;
  user: {
    id: string;
    username: string;
    email: string | null;
    displayName: string | null;
  };
}

export interface AdminPasskey {
  id: string;
  name: string;
  deviceType: 'singleDevice' | 'multiDevice' | string;
  backedUp: boolean;
  transports: string[];
  createdAt: string;
  lastUsedAt: string | null;
}

// Status Log types
export type StatusLogAction = 'SUSPEND' | 'BLOCK' | 'LIFT' | 'AUTO_LIFT' | 'RESTRICT' | 'ACTIVATE';
export type StatusLogPerformerType = 'ADMIN' | 'SYSTEM' | 'USER';

export interface StatusLogEntry {
  id: string;
  userId: string;
  action: StatusLogAction;
  previousStatus: UserStatus | null;
  newStatus: UserStatus;
  reason: string | null;
  suspendedUntil: string | null;
  performedBy: string | null;
  performerType: StatusLogPerformerType;
  ipAddress: string | null;
  userAgent: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

// Notification types
export type NotificationLevel = 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS';
export type NotificationPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
export type NotificationTargetType = 'ALL' | 'SPECIFIC';

export interface Notification {
  id: string;
  title: MultiLangText;
  message: MultiLangText;
  level: NotificationLevel;
  priority: NotificationPriority;
  target_type: NotificationTargetType;
  target_user_ids: string[];
  expires_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  isExpired: boolean; // Server-side calculated expiration status
}

export interface CreateNotificationPayload {
  title: MultiLangText;
  message: MultiLangText;
  level: NotificationLevel;
  priority: NotificationPriority;
  targetType: NotificationTargetType;
  targetUserIds?: string[];
  expiresAt?: string;
}

// OGP types
export interface OgpPreview {
  url: string;
  title?: string;
  description?: string;
  siteName?: string;
  imageUrl?: string;
  /** R2 に保存した OGP 画像。画像がない、または保存に失敗した場合は null */
  storage: { key: string; size: number; mimeType: string } | null;
  storageError?: string;
}

// Banner types
export type BannerLevel = 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS';
export type BannerStatus = 'ACTIVE' | 'SCHEDULED' | 'EXPIRED' | 'INACTIVE';

/**
 * Supported languages for multi-language banners
 */
export const SUPPORTED_LANGUAGE_CODES = [
  'en',
  'ja',
  'es',
  'fr',
  'ko',
  'de',
  'pt',
  'tr',
  'zh-cn',
  'zh-tw',
  'it'
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGE_CODES)[number];

/**
 * Multi-language text object
 */
export type MultiLangText = Partial<Record<SupportedLanguage, string>>;

export interface Banner {
  id: string;
  title: MultiLangText; // Multi-language titles: { "en": "...", "ja": "...", ... }
  message: MultiLangText; // Multi-language messages: { "en": "...", "ja": "...", ... }
  level: BannerLevel;
  priority: number;
  start_at: string;
  end_at: string;
  link_url: MultiLangText | null; // Multi-language link URLs (optional)
  link_text: MultiLangText | null; // Multi-language link texts (optional)
  hidden_languages: string[]; // Languages that should NOT see this banner
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  status: BannerStatus; // Server-side calculated status
}

export interface CreateBannerPayload {
  title: MultiLangText;
  message: MultiLangText;
  level: BannerLevel;
  priority: number;
  startAt: string;
  endAt: string;
  linkUrl?: MultiLangText | null;
  linkText?: MultiLangText | null;
  hiddenLanguages?: string[];
}

export interface UpdateBannerPayload {
  title?: MultiLangText;
  message?: MultiLangText;
  level?: BannerLevel;
  priority?: number;
  startAt?: string;
  endAt?: string;
  linkUrl?: MultiLangText | null;
  linkText?: MultiLangText | null;
  hiddenLanguages?: string[];
}

// AT Protocol types for better type safety
export type Facet = AppBskyRichtextFacet.Main;
export type EmbedImages = AppBskyEmbedImages.Main;

// AppBskyEmbedExternal.Main を拡張して thumbUrl（予約段階の一時サムネイルURL）を含む
export interface EmbedExternal extends AppBskyEmbedExternal.Main {
  external: AppBskyEmbedExternal.Main['external'] & {
    thumbUrl?: string;
  };
}

export type Embed = EmbedImages | EmbedExternal | EmbedVideo | Record<string, unknown>;

// Type guard functions for embed types
export function isEmbedImages(embed: unknown): embed is EmbedImages {
  const e = embed as Record<string, unknown> | null | undefined;
  return e != null && e.$type === 'app.bsky.embed.images' && Array.isArray(e.images);
}

export function isEmbedExternal(embed: unknown): embed is EmbedExternal {
  const e = embed as Record<string, unknown> | null | undefined;
  return e != null && e.$type === 'app.bsky.embed.external' && e.external != null;
}

export interface EmbedVideo {
  $type: 'app.bsky.embed.video';
  video: {
    ref?: { $link?: string };
    key?: string;
    mimeType?: string;
    size?: number;
  };
  alt?: string;
  aspectRatio?: { width: number; height: number };
}

export function isEmbedVideo(embed: unknown): embed is EmbedVideo {
  const e = embed as Record<string, unknown> | null | undefined;
  return e != null && e.$type === 'app.bsky.embed.video' && e.video != null;
}

// ThreadGate rule: legacy string format ('mention' | 'follower' | 'following')
// or AT Protocol lexicon object format ({ $type: 'app.bsky.feed.threadgate#mentionRule' } など)
// 本番 DB には両形式が混在するため union で扱う
export type ThreadgateRule = string | { $type: string };

// Scheduler Authorization types
export interface SchedulerAuthorization {
  authorized: boolean;
  token: {
    id: string;
    is_active: boolean;
    refresh_expires_at: string;
    created_at: string;
    updated_at: string;
    last_used_at: string | null;
  } | null;
}

// Plan usage response type - mirrors GetPlanUsageOutput in
// packages/frontend/src/components/PlanUsageDisplay.tsx (same API contract as
// GET /api/settings/plan/usage, served here via GET /api/admin/users/:userId/usage)
export interface PlanUsageInfo {
  currentPlan: {
    id: string;
    tier: string;
    name: string;
    displayName: Record<string, string>;
    validUntil: string | null;
    isActive: boolean;
  };
  limits: {
    monthlyPostsLimit: number;
    pendingPostsLimit: number;
    pendingDeleteActionsLimit: number;
    actionsPerSlotLimit: number;
    maxScheduleDays: number;
    markdownSupport: boolean;
    scheduleIntervalMinutes: number;
    maxImagesPerPost: number;
    maxImageSizeMb: number;
    threadPosts: boolean;
    threadPostsLimit: number;
    videoUpload: boolean;
    maxVideoSizeMb: number;
    videoProcessingMinutesMonthly: number;
  };
  usage: {
    pendingPostsCount: number;
    monthlyPostsCount: number;
    monthlyPeriodStart: string;
    monthlyPeriodEnd: string;
    storageUsedMb: number;
    apiRequestsThisHour: number;
    lastUpdated: string;
    // 予約アクション機能で追加されたフィールド。バックエンドが未対応の場合は
    // undefined になりうるため、利用側は必ず防御的に扱うこと。
    pendingRepostsCount?: number;
    pendingDeleteActionsCount?: number;
  };
}

// Reserved action types - GET /api/admin/users/:userId/reserved-actions のレスポンス契約
export type ReservedActionKind = 'POST' | 'REPOST' | 'DELETE_POST';
export type ReservedActionStatus = 'PENDING' | 'EXECUTING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

export interface ReservedAction {
  id: string;
  kind: ReservedActionKind;
  status: ReservedActionStatus;
  scheduledAt: string;
  createdAt: string;
  executedAt: string | null;
  errorMsg: string | null;
  // kind === 'POST' のときのみ入る（予約投稿の本文）
  text: string | null;
  // 予約アクション（REPOST / DELETE_POST）のときのみ入る
  targetAtUri: string | null;
  targetPostText: string | null;
  resultAtUri: string | null;
  // グローバル一覧（GET /api/admin/reserved-actions）でのみ入る。
  // ユーザー詳細ページ側のエンドポイントはユーザーが確定しているため付与されない。
  userId?: string;
  author?: {
    did: string;
    handle: string;
    displayName: string | null;
    avatar: string | null;
  };
}

export interface ReservedActionsResponse {
  items: ReservedAction[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// POST /api/admin/scheduled-actions/:actionId/cancel のレスポンス契約（scheduled_actions の行をそのまま返す）
export interface CancelledScheduledAction {
  id: string;
  user_id: string;
  action_type: string;
  target_at_uri: string;
  target_at_cid: string | null;
  target_author_did: string | null;
  scheduled_at: string;
  status: string;
  result_at_uri: string | null;
  result_at_rkey: string | null;
  error_msg: string | null;
  retry_count: number;
  created_at: string;
  updated_at: string;
  executed_at: string | null;
  target_post_text: string | null;
  deleted_at: string | null;
}

export class AdminAPIClient {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('admin_token');
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('admin_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('admin_token');
  }

  isAuthenticated(): boolean {
    return this.token !== null;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit & { skipAuth?: boolean } = {}
  ): Promise<T> {
    const { skipAuth, ...fetchOptions } = options;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    // Add authorization header unless skipAuth is true
    if (!skipAuth && this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...fetchOptions,
      headers: {
        ...headers,
        ...(fetchOptions.headers as Record<string, string>)
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));

      // Handle workers-api error format: { success: false, error: string, message: string, details: any }
      if (error.details) {
        // Handle array format (validation errors from schemas)
        if (Array.isArray(error.details) && error.details.length > 0) {
          const detailMessages = error.details
            .map((d: any) => `${d.path?.join('.')}: ${d.message}`)
            .join(', ');
          throw new Error(`${error.message || 'Validation failed'}: ${detailMessages}`);
        }
        // Handle string format (custom validation errors)
        if (typeof error.details === 'string') {
          const enhancedError = new Error(error.error || error.message || 'Request failed');
          (enhancedError as any).details = error.details;
          throw enhancedError;
        }
      }

      throw new Error(error.error ?? error.message ?? `HTTP ${response.status}`);
    }

    const json = await response.json();

    // Handle workers-api success format: { success: true, data: {...} }
    // Unwrap the 'data' property if it exists
    if (json.success === true && json.data !== undefined) {
      return json.data;
    }

    return json;
  }

  private _buildQueryString(params: Record<string, any>): string {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        query.set(key, String(value));
      }
    });
    const queryString = query.toString();
    return queryString ? `?${queryString}` : '';
  }

  // ===== Authentication =====

  // Passkey login (usernameless, discoverable credentials)
  async getPasskeyLoginOptions(): Promise<{
    challengeId: string;
    options: PublicKeyCredentialRequestOptionsJSON;
  }> {
    return this.request('/api/admin/auth/passkey/login/options', {
      method: 'POST',
      body: JSON.stringify({}),
      skipAuth: true
    });
  }

  async verifyPasskeyLogin(
    challengeId: string,
    response: AuthenticationResponseJSON
  ): Promise<AdminAuthResult> {
    return this.request('/api/admin/auth/passkey/login/verify', {
      method: 'POST',
      body: JSON.stringify({ challengeId, response }),
      skipAuth: true
    });
  }

  // Passkey registration with a one-time token (bootstrap / recovery)
  async getPasskeyTokenRegistrationOptions(token: string): Promise<{
    challengeId: string;
    options: PublicKeyCredentialCreationOptionsJSON;
  }> {
    return this.request('/api/admin/auth/passkey/register/token/options', {
      method: 'POST',
      body: JSON.stringify({ token }),
      skipAuth: true
    });
  }

  async verifyPasskeyTokenRegistration(params: {
    token: string;
    challengeId: string;
    response: RegistrationResponseJSON;
    name: string;
  }): Promise<AdminAuthResult> {
    return this.request('/api/admin/auth/passkey/register/token/verify', {
      method: 'POST',
      body: JSON.stringify(params),
      skipAuth: true
    });
  }

  // Passkey management (authenticated)
  async listPasskeys(): Promise<{ passkeys: AdminPasskey[] }> {
    return this.request('/api/admin/auth/passkeys');
  }

  async getPasskeyRegistrationOptions(): Promise<{
    challengeId: string;
    options: PublicKeyCredentialCreationOptionsJSON;
  }> {
    return this.request('/api/admin/auth/passkeys/register/options', {
      method: 'POST',
      body: JSON.stringify({})
    });
  }

  async verifyPasskeyRegistration(params: {
    challengeId: string;
    response: RegistrationResponseJSON;
    name: string;
  }): Promise<{ passkey: AdminPasskey }> {
    return this.request('/api/admin/auth/passkeys/register/verify', {
      method: 'POST',
      body: JSON.stringify(params)
    });
  }

  async renamePasskey(id: string, name: string): Promise<{ passkey: AdminPasskey }> {
    return this.request(`/api/admin/auth/passkeys/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ name })
    });
  }

  async deletePasskey(id: string): Promise<{ success: boolean }> {
    return this.request(`/api/admin/auth/passkeys/${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
  }

  async getCurrentUser(): Promise<{
    id: string;
    username: string;
    email: string | null;
    displayName: string | null;
  }> {
    return this.request('/api/admin/auth/me');
  }

  // ===== Service Settings =====

  async getServiceSettings(): Promise<{
    id: number;
    is_closed_beta: boolean;
    is_service_suspended: boolean;
    updated_at: string;
  }> {
    return this.request('/api/admin/settings');
  }

  async updateServiceSettings(settings: {
    is_closed_beta?: boolean;
    is_service_suspended?: boolean;
  }): Promise<{
    id: number;
    is_closed_beta: boolean;
    is_service_suspended: boolean;
    updated_at: string;
  }> {
    return this.request('/api/admin/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings)
    });
  }

  // ===== Dashboard Statistics =====

  async getDashboardStats(): Promise<{
    posts: {
      total: number;
      byStatus: { status: string; count: number }[];
      dailyScheduledPosts: {
        date: string;
        byStatus: { status: string; count: number }[];
      }[];
    };
    users: {
      total: number;
      activeUsers: number;
      dailyRegistrations: { date: string; count: number }[];
    };
    // 予約アクション機能で追加されたフィールド。バックエンドのデプロイ前は
    // undefined になりうるため、利用側は必ず防御的に扱うこと。
    actions?: {
      total: number;
      byKind: { kind: string; count: number }[];
      byStatus: { status: string; count: number }[];
      dailyScheduledActions: {
        date: string;
        byStatus: { status: string; count: number }[];
      }[];
    };
  }> {
    return this.request('/api/admin/stats');
  }

  // ===== User Management =====

  async getUsers(params?: {
    limit?: number;
    offset?: number;
    status?: string;
    search?: string;
  }): Promise<{
    users: Array<{
      id: string;
      did: string;
      handle: string;
      display_name: string | null;
      avatar: string | null;
      created_at: string;
      updated_at: string;
      is_active: boolean;
      has_access_keyword_access: boolean;
      status: UserStatus;
      upgrade_prohibited: boolean;
      available_reservations: number | null;
    }>;
    total: number;
  }> {
    const query = new URLSearchParams();
    if (params?.limit) {
      query.set('limit', params.limit.toString());
    }
    if (params?.offset) {
      query.set('offset', params.offset.toString());
    }
    if (params?.status) {
      query.set('status', params.status);
    }
    if (params?.search) {
      query.set('search', params.search);
    }

    return this.request(`/api/admin/users?${query}`);
  }

  async getUser(userId: string): Promise<{
    id: string;
    did: string;
    handle: string;
    display_name: string | null;
    avatar: string | null;
    created_at: string;
    updated_at: string;
    is_active: boolean;
    has_access_keyword_access: boolean;
    status: UserStatus;
    upgrade_prohibited: boolean;
  }> {
    return this.request(`/api/admin/users/${userId}`);
  }

  async updateUser(
    userId: string,
    updates: {
      status?: string;
      is_active?: boolean;
      display_name?: string;
      has_access_keyword_access?: boolean;
    }
  ): Promise<{
    id: string;
    did: string;
    handle: string;
    display_name: string | null;
    avatar: string | null;
    created_at: string;
    updated_at: string;
    is_active: boolean;
    has_access_keyword_access: boolean;
    status: UserStatus;
    upgrade_prohibited: boolean;
  }> {
    return this.request(`/api/admin/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
  }

  async getUserPosts(
    userId: string,
    params?: { limit?: number; offset?: number; status?: string }
  ): Promise<{
    posts: Array<{
      id: string;
      user_id: string;
      content: string;
      scheduled_at: string;
      status: string;
      created_at: string;
      updated_at: string;
      executed_at: string | null;
      error_msg: string | null;
      retry_count: number;
      at_uri: string | null;
      at_rkey: string | null;
      author?: {
        did: string;
        handle: string;
        display_name: string | null;
        avatar: string | null;
      };
    }>;
    total: number;
  }> {
    const query = new URLSearchParams();
    if (params?.limit) {
      query.set('limit', params.limit.toString());
    }
    if (params?.offset) {
      query.set('offset', params.offset.toString());
    }
    if (params?.status) {
      query.set('status', params.status);
    }

    return this.request(`/api/admin/users/${userId}/posts?${query}`);
  }

  async getUserReservedActions(
    userId: string,
    params?: { page?: number; limit?: number; status?: string; kind?: string }
  ): Promise<ReservedActionsResponse> {
    const queryString = this._buildQueryString(params ?? {});
    return this.request(`/api/admin/users/${userId}/reserved-actions${queryString}`);
  }

  // 全ユーザー横断の予約アクション一覧（Reserved Actions ページ用）
  async getReservedActions(params?: {
    page?: number;
    limit?: number;
    status?: string;
    kind?: string;
    /** ユーザー ID の完全一致 */
    userId?: string;
    /** DID / handle の部分一致（既存の投稿一覧と同じ絞り込み） */
    userFilter?: string;
    sortField?: 'scheduled_at' | 'created_at' | 'status';
    sortOrder?: 'asc' | 'desc';
  }): Promise<ReservedActionsResponse> {
    const queryString = this._buildQueryString(params ?? {});
    return this.request(`/api/admin/reserved-actions${queryString}`);
  }

  async getUserPlanAssignments(userId: string): Promise<{
    assignments: Array<{
      id: string;
      planId: string;
      plan: {
        id: string;
        tier: string;
        name: string;
        displayName: Record<string, string>;
        limits: Record<string, number | boolean | null>;
      };
      ticketId: string | null;
      ticket: {
        id: string;
        code: string;
      } | null;
      activatedAt: string;
      expiresAt: string | null;
      status: string;
      createdAt: string;
    }>;
  }> {
    return this.request(`/api/admin/users/${userId}/plan-assignments`);
  }

  async revokeUserPlanAssignment(
    userId: string,
    assignmentId: string
  ): Promise<{
    assignment: {
      id: string;
      status: string;
    };
    message: string;
  }> {
    return this.request(`/api/admin/users/${userId}/plan-assignments/${assignmentId}/revoke`, {
      method: 'POST'
    });
  }

  async updateUserRestrictions(
    userId: string,
    restrictions: {
      upgradeProhibited?: boolean;
    }
  ): Promise<{
    id: string;
    upgradeProhibited: boolean;
    message: string;
  }> {
    return this.request(`/api/admin/users/${userId}/restrictions`, {
      method: 'PATCH',
      body: JSON.stringify(restrictions)
    });
  }

  async setSuspension(
    userId: string,
    payload: {
      status: 'SUSPENDED' | 'BLOCKED';
      suspensionType: 'temporary' | 'permanent';
      days?: number;
      until?: string;
      reason?: string;
    }
  ): Promise<{
    user: {
      id: string;
      handle: string;
      status: UserStatus;
      suspendedUntil: string | null;
      suspensionReason: string | null;
    };
    message: string;
  }> {
    return this.request(`/api/admin/users/${userId}/suspension`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async liftSuspension(userId: string): Promise<{
    message: string;
    user: {
      id: string;
      handle: string;
      status: string;
    };
  }> {
    return this.request(`/api/admin/users/${userId}/suspension`, {
      method: 'DELETE'
    });
  }

  async getSuspendedUsers(includeExpired?: boolean): Promise<{
    users: Array<{
      id: string;
      did: string;
      handle: string;
      status: UserStatus;
      suspendedUntil: string | null;
      suspensionReason: string | null;
      createdAt: string;
      updatedAt: string;
    }>;
  }> {
    const queryString = this._buildQueryString({ includeExpired });
    return this.request(`/api/admin/users/suspended${queryString}`);
  }

  // ===== Status Logs =====

  async getUserStatusLogs(
    userId: string,
    params?: {
      limit?: number;
      offset?: number;
      action?: StatusLogAction;
    }
  ): Promise<{
    logs: StatusLogEntry[];
    total: number;
  }> {
    const queryString = this._buildQueryString(params ?? {});
    return this.request(`/api/admin/users/${userId}/status-logs${queryString}`);
  }

  async getAllStatusLogs(params?: {
    limit?: number;
    offset?: number;
    action?: StatusLogAction;
  }): Promise<{
    logs: Array<StatusLogEntry & { user: { handle: string; displayName: string | null } }>;
    total: number;
  }> {
    const queryString = this._buildQueryString(params ?? {});
    return this.request(`/api/admin/status-logs${queryString}`);
  }

  // ===== Post Management =====

  async getUserUsage(userId: string): Promise<PlanUsageInfo> {
    return this.request(`/api/admin/users/${userId}/usage`);
  }

  async getPostThread(postId: string): Promise<{
    posts: Array<{
      id: string;
      user_id: string;
      content: string;
      scheduled_at: string;
      status: string;
      created_at: string;
      updated_at: string;
      executed_at: string | null;
      at_uri: string | null;
      at_rkey: string | null;
      error_msg: string | null;
      languages: string[] | null;
      facets: Facet[] | null;
      embed: Embed | null;
      parent_post_id: string | null;
    }>;
  }> {
    return this.request(`/api/admin/posts/${postId}/thread`);
  }

  async getAllPosts(params?: {
    limit?: number;
    offset?: number;
    status?: string;
    userFilter?: string;
    sort?: 'scheduled_at' | 'created_at' | 'status';
    order?: 'asc' | 'desc';
  }): Promise<{
    posts: Array<{
      id: string;
      user_id: string;
      content: string;
      scheduled_at: string;
      status: string;
      created_at: string;
      updated_at: string;
      executed_at: string | null;
      error_msg: string | null;
      retry_count: number;
      at_uri: string | null;
      at_rkey: string | null;
      thread_count?: number;
      has_failed_thread?: boolean;
      author?: {
        did: string;
        handle: string;
        display_name: string | null;
        avatar: string | null;
      };
    }>;
    total: number;
  }> {
    const query = new URLSearchParams();
    if (params?.limit) {
      query.set('limit', params.limit.toString());
    }
    if (params?.offset) {
      query.set('offset', params.offset.toString());
    }
    if (params?.status) {
      query.set('status', params.status);
    }
    if (params?.userFilter) {
      query.set('userFilter', params.userFilter);
    }
    if (params?.sort) {
      query.set('sort', params.sort);
    }
    if (params?.order) {
      query.set('order', params.order);
    }

    return this.request(`/api/admin/posts?${query}`);
  }

  async deletePost(postId: string): Promise<{ success: boolean }> {
    return this.request(`/api/admin/posts/${postId}`, {
      method: 'DELETE'
    });
  }

  async cancelPost(postId: string): Promise<{
    id: string;
    user_id: string;
    content: string;
    scheduled_at: string;
    status: string;
    created_at: string;
    updated_at: string;
    executed_at: string | null;
    error_msg: string | null;
    retry_count: number;
    at_uri: string | null;
    at_rkey: string | null;
  }> {
    return this.request(`/api/admin/posts/${postId}/cancel`, {
      method: 'POST'
    });
  }

  // 予約アクション（予約リポスト / 予約削除）のキャンセル（管理者用）
  // 利用者向けの削除とは別物で、実行を止めるだけでレコードは隠さない
  async cancelScheduledAction(actionId: string): Promise<CancelledScheduledAction> {
    return this.request(`/api/admin/scheduled-actions/${actionId}/cancel`, {
      method: 'POST'
    });
  }

  async getPost(postId: string): Promise<{
    id: string;
    user_id: string;
    content: string;
    scheduled_at: string;
    status: string;
    created_at: string;
    updated_at: string;
    executed_at: string | null;
    at_uri: string | null;
    at_rkey: string | null;
    error_msg: string | null;
    languages: string[] | null;
    facets: Facet[] | null;
    embed: Embed | null;
    threadgate_rules: ThreadgateRule[] | null;
    threadgate_uri: string | null;
    threadgate_cid: string | null;
    postgate_disable_embedding: boolean | null;
    postgate_uri: string | null;
    postgate_cid: string | null;
    content_labels: {
      $type: 'com.atproto.label.defs#selfLabels';
      values: Array<{ val: string }>;
    } | null;
  }> {
    return this.request(`/api/admin/posts/${postId}`);
  }

  async updatePost(
    postId: string,
    updates: { status?: string }
  ): Promise<{
    id: string;
    user_id: string;
    content: string;
    scheduled_at: string;
    status: string;
    created_at: string;
    updated_at: string;
    executed_at: string | null;
    at_uri: string | null;
    at_rkey: string | null;
    error_msg: string | null;
    languages: string[] | null;
    facets: Facet[] | null;
    embed: Embed | null;
    threadgate_rules: ThreadgateRule[] | null;
    threadgate_uri: string | null;
    threadgate_cid: string | null;
    postgate_disable_embedding: boolean | null;
    postgate_uri: string | null;
    postgate_cid: string | null;
    content_labels: {
      $type: 'com.atproto.label.defs#selfLabels';
      values: Array<{ val: string }>;
    } | null;
  }> {
    return this.request(`/api/admin/posts/${postId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
  }

  // ===== Ticket Management =====

  async issueTicket(request: {
    planId: string;
    validDays: number;
    planExpiresAt?: string;
    maxRedemptions?: number;
    redemptionDeadline?: string;
    memo?: string;
    limitsOverride?: Record<string, number | boolean | null>;
  }): Promise<{
    ticket: {
      id: string;
      code: string;
      planId: string;
      redemptionDeadline: string | null;
      validDays: number;
      planExpiresAt: string | null;
      status: string;
    };
  }> {
    return this.request('/api/admin/tickets/issue', {
      method: 'POST',
      body: JSON.stringify(request)
    });
  }

  async getTickets(): Promise<{
    tickets: Array<{
      id: string;
      code: string;
      planId: string;
      plan: {
        id: string;
        tier: string;
        name: string;
        displayName: Record<string, string>;
      };
      issuer: {
        id: string;
        did: string;
        handle: string;
        displayName: string | null;
      };
      issuedAt: string;
      redemptionDeadline: string | null;
      validDays: number;
      planExpiresAt: string | null;
      maxRedemptions: number | null;
      redemptionCount: number;
      status: string;
      memo: string | null;
    }>;
  }> {
    return this.request('/api/admin/tickets');
  }

  async getTicket(ticketId: string): Promise<{
    ticket: {
      id: string;
      code: string;
      planId: string;
      plan: {
        id: string;
        tier: string;
        name: string;
        displayName: Record<string, string>;
        limits: Record<string, number | boolean | null>;
      };
      issuer: {
        id: string;
        did: string;
        handle: string;
        displayName: string | null;
      };
      issuedAt: string;
      redemptionDeadline: string | null;
      validDays: number;
      planExpiresAt: string | null;
      maxRedemptions: number | null;
      status: string;
      memo: string | null;
      limitsOverride: Record<string, number | boolean | null> | null;
      createdAt: string;
      updatedAt: string;
    };
    assignments: Array<{
      id: string;
      user: {
        id: string;
        did: string;
        handle: string;
        displayName: string | null;
      };
      activatedAt: string;
      expiresAt: string | null;
      status: string;
      createdAt: string;
    }>;
  }> {
    return this.request(`/api/admin/tickets/${ticketId}`);
  }

  async revokeTicket(ticketId: string): Promise<{
    ticket: {
      id: string;
      code: string;
      status: string;
    };
    message: string;
  }> {
    return this.request(`/api/admin/tickets/${ticketId}/revoke`, {
      method: 'POST'
    });
  }

  // ===== Tier Management =====

  async getTiers(params?: { includeInactive?: boolean }): Promise<{
    tiers: Array<{
      id: string;
      key: string;
      name: string;
      displayName: Record<string, string>;
      description: Record<string, string> | null;
      displayOrder: number;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    }>;
  }> {
    const query = new URLSearchParams();
    if (params?.includeInactive) {
      query.set('includeInactive', 'true');
    }
    const queryString = query.toString();
    return this.request(`/api/admin/tiers${queryString ? `?${queryString}` : ''}`);
  }

  async getTier(tierId: string): Promise<{
    tier: {
      id: string;
      key: string;
      name: string;
      displayName: Record<string, string>;
      description: Record<string, string> | null;
      displayOrder: number;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    return this.request(`/api/admin/tiers/${tierId}`);
  }

  async createTier(request: {
    key: string;
    name: string;
    displayName: Record<string, string>;
    description?: Record<string, string> | null;
    displayOrder?: number;
    isActive?: boolean;
  }): Promise<{
    tier: {
      id: string;
      key: string;
      name: string;
      displayName: Record<string, string>;
      description: Record<string, string> | null;
      displayOrder: number;
      isActive: boolean;
    };
  }> {
    return this.request('/api/admin/tiers', {
      method: 'POST',
      body: JSON.stringify(request)
    });
  }

  async updateTier(
    tierId: string,
    updates: {
      name?: string;
      displayName?: Record<string, string>;
      description?: Record<string, string> | null;
      displayOrder?: number;
      isActive?: boolean;
    }
  ): Promise<{
    tier: {
      id: string;
      key: string;
      name: string;
      displayName: Record<string, string>;
      description: Record<string, string> | null;
      displayOrder: number;
      isActive: boolean;
    };
  }> {
    return this.request(`/api/admin/tiers/${tierId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
  }

  async deleteTier(tierId: string): Promise<void> {
    return this.request(`/api/admin/tiers/${tierId}`, { method: 'DELETE' });
  }

  // ===== Plan Management =====

  async getPlans(params?: { includeInactive?: boolean; includeInvisible?: boolean }): Promise<{
    plans: Array<{
      id: string;
      tier: string;
      name: string;
      displayName: Record<string, string>;
      description: Record<string, string>;
      prices: Array<{
        id: string;
        currencyCode: string;
        priceMonthly: number | null;
        priceYearly: number | null;
      }>;
      limits: Record<string, number | boolean | null>;
      features: Record<string, string[]>;
      displayOrder: number;
      isActive: boolean;
      isVisible: boolean;
    }>;
  }> {
    const query = new URLSearchParams();
    if (params?.includeInactive) {
      query.set('includeInactive', 'true');
    }
    if (params?.includeInvisible) {
      query.set('includeInvisible', 'true');
    }

    const queryString = query.toString();
    return this.request(`/api/admin/plans${queryString ? `?${queryString}` : ''}`);
  }

  async getPlan(planId: string): Promise<{
    plan: {
      id: string;
      tier: string;
      name: string;
      displayName: Record<string, string>;
      description: Record<string, string>;
      prices: Array<{
        id: string;
        currencyCode: string;
        priceMonthly: number | null;
        priceYearly: number | null;
      }>;
      limits: Record<string, number | boolean | null>;
      features: Record<string, string[]>;
      displayOrder: number;
      isActive: boolean;
      isVisible: boolean;
    };
  }> {
    return this.request(`/api/admin/plans/${planId}`);
  }

  async createPlan(request: {
    tier: string;
    name: string;
    displayName: Record<string, string>;
    description: Record<string, string>;
    prices: Array<{
      id: string;
      currencyCode: string;
      priceMonthly: number | null;
      priceYearly: number | null;
    }>;
    limits: Record<string, number | boolean | null>;
    features: Record<string, string[]>;
    displayOrder: number;
    isActive?: boolean;
    isVisible?: boolean;
  }): Promise<{
    plan: {
      id: string;
      tier: string;
      name: string;
      displayName: Record<string, string>;
      description: Record<string, string>;
      prices: Array<{
        id: string;
        currencyCode: string;
        priceMonthly: number | null;
        priceYearly: number | null;
      }>;
      limits: Record<string, number | boolean | null>;
      features: Record<string, string[]>;
      displayOrder: number;
      isActive: boolean;
      isVisible: boolean;
    };
  }> {
    return this.request('/api/admin/plans', {
      method: 'POST',
      body: JSON.stringify(request)
    });
  }

  async updatePlan(
    planId: string,
    updates: {
      name?: string;
      displayName?: Record<string, string>;
      description?: Record<string, string>;
      prices?: Array<{
        id: string;
        currencyCode: string;
        priceMonthly: number | null;
        priceYearly: number | null;
      }>;
      limits?: Record<string, number | boolean | null>;
      features?: Record<string, string[]>;
      displayOrder?: number;
      isActive?: boolean;
      isVisible?: boolean;
    }
  ): Promise<{
    plan: {
      id: string;
      tier: string;
      name: string;
      displayName: Record<string, string>;
      description: Record<string, string>;
      prices: Array<{
        id: string;
        currencyCode: string;
        priceMonthly: number | null;
        priceYearly: number | null;
      }>;
      limits: Record<string, number | boolean | null>;
      features: Record<string, string[]>;
      displayOrder: number;
      isActive: boolean;
      isVisible: boolean;
    };
  }> {
    return this.request(`/api/admin/plans/${planId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  }

  async togglePlanVisibility(
    planId: string,
    isVisible: boolean
  ): Promise<{
    plan: {
      id: string;
      tier: string;
      name: string;
      isVisible: boolean;
    };
  }> {
    return this.request(`/api/admin/plans/${planId}/visibility`, {
      method: 'PATCH',
      body: JSON.stringify({ isVisible })
    });
  }

  async togglePlanActive(
    planId: string,
    isActive: boolean
  ): Promise<{
    plan: {
      id: string;
      tier: string;
      name: string;
      isActive: boolean;
    };
  }> {
    return this.request(`/api/admin/plans/${planId}/active`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive })
    });
  }

  async publishPlans(): Promise<{
    success: boolean;
    message: string;
    listUrl: string;
    individualUrls: string[];
    publishedCount: number;
  }> {
    return this.request('/api/admin/plans/publish', {
      method: 'POST'
    });
  }

  // ===== Currency Management =====

  async getCurrencies(includeInactive?: boolean): Promise<{
    currencies: Array<{
      id: string;
      code: string;
      name: string;
      symbol: string;
      useDecimals: boolean;
      decimalPlaces: number;
      displayOrder: number;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
  }> {
    const query = includeInactive ? '?includeInactive=true' : '';
    return this.request(`/api/admin/currencies${query}`);
  }

  async getCurrency(currencyId: string): Promise<{
    currency: {
      id: string;
      code: string;
      name: string;
      symbol: string;
      useDecimals: boolean;
      decimalPlaces: number;
      displayOrder: number;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    return this.request(`/api/admin/currencies/${currencyId}`);
  }

  async createCurrency(request: {
    code: string;
    name: string;
    symbol: string;
    useDecimals: boolean;
    decimalPlaces: number;
    displayOrder?: number;
    isActive?: boolean;
  }): Promise<{
    currency: {
      id: string;
      code: string;
      name: string;
      symbol: string;
      useDecimals: boolean;
      decimalPlaces: number;
      displayOrder: number;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    return this.request('/api/admin/currencies', {
      method: 'POST',
      body: JSON.stringify(request)
    });
  }

  async updateCurrency(
    currencyId: string,
    updates: {
      name?: string;
      symbol?: string;
      useDecimals?: boolean;
      decimalPlaces?: number;
      displayOrder?: number;
      isActive?: boolean;
    }
  ): Promise<{
    currency: {
      id: string;
      code: string;
      name: string;
      symbol: string;
      useDecimals: boolean;
      decimalPlaces: number;
      displayOrder: number;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    return this.request(`/api/admin/currencies/${currencyId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
  }

  async deleteCurrency(currencyId: string): Promise<{ message: string }> {
    return this.request(`/api/admin/currencies/${currencyId}`, {
      method: 'DELETE'
    });
  }

  async activateCurrency(currencyId: string): Promise<{
    currency: {
      id: string;
      code: string;
      name: string;
      symbol: string;
      useDecimals: boolean;
      decimalPlaces: number;
      displayOrder: number;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    return this.request(`/api/admin/currencies/${currencyId}/activate`, {
      method: 'POST'
    });
  }

  // ===== Scheduler Sessions =====

  async getSchedulerSessionStats(): Promise<{
    success: boolean;
    activeCount: number;
    totalCount: number;
    expiringSoon: number;
  }> {
    return this.request('/api/admin/scheduler-sessions/stats', {
      method: 'GET'
    });
  }

  async bulkRevokeSchedulerSessions(beforeDate?: Date): Promise<{
    success: boolean;
    message: string;
    revokedCount: number;
    affectedUserCount: number;
    affectedUserIds: string[];
  }> {
    return this.request('/api/admin/scheduler-sessions/bulk-revoke', {
      method: 'POST',
      body: JSON.stringify({
        beforeDate: beforeDate?.toISOString()
      })
    });
  }

  // ===== User Scheduler Authorization =====

  async getUserSchedulerAuthorization(userId: string): Promise<SchedulerAuthorization> {
    return this.request(`/api/admin/users/${userId}/scheduler`, {
      method: 'GET'
    });
  }

  async revokeUserSchedulerAuthorization(userId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request(`/api/admin/users/${userId}/scheduler/revoke`, {
      method: 'POST'
    });
  }

  // ===== Notification Management =====

  async getNotifications(): Promise<{ notifications: Notification[] }> {
    return this.request('/api/admin/notifications');
  }

  async getNotificationStats(): Promise<{
    totalCount: number;
    activeCount: number;
    expiredCount: number;
  }> {
    return this.request('/api/admin/notifications/stats');
  }

  async createNotification(
    payload: CreateNotificationPayload
  ): Promise<{ notification: Notification }> {
    return this.request('/api/admin/notifications', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async deleteNotification(notificationId: string): Promise<{ success: boolean }> {
    return this.request(`/api/admin/notifications/${notificationId}`, {
      method: 'DELETE'
    });
  }

  // ===== OGP =====

  /** URL の OGP を取得し、OGP 画像を R2 に保存する */
  async fetchOgp(url: string): Promise<OgpPreview> {
    return this.request('/api/admin/ogp', {
      method: 'POST',
      body: JSON.stringify({ url })
    });
  }

  // ===== Banner Management =====

  async getBanners(params?: { includeInactive?: boolean }): Promise<{ banners: Banner[] }> {
    const query = new URLSearchParams();
    if (params?.includeInactive !== undefined) {
      query.set('includeInactive', String(params.includeInactive));
    }
    const queryString = query.toString();
    return this.request(`/api/admin/banners${queryString ? `?${queryString}` : ''}`);
  }

  async getBannerStats(): Promise<{
    totalCount: number;
    activeCount: number;
    scheduledCount: number;
    expiredCount: number;
  }> {
    return this.request('/api/admin/banners/stats');
  }

  async createBanner(payload: CreateBannerPayload): Promise<{ banner: Banner }> {
    return this.request('/api/admin/banners', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async updateBanner(bannerId: string, payload: UpdateBannerPayload): Promise<{ banner: Banner }> {
    return this.request(`/api/admin/banners/${bannerId}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
  }

  async deactivateBanner(bannerId: string): Promise<{ banner: Banner }> {
    return this.request(`/api/admin/banners/${bannerId}/deactivate`, {
      method: 'POST'
    });
  }

  async deleteBanner(bannerId: string): Promise<{ success: boolean }> {
    return this.request(`/api/admin/banners/${bannerId}`, {
      method: 'DELETE'
    });
  }
}

export const apiClient = new AdminAPIClient();
